import { Button, Chip, GenericSuspense, Table, useRuntimeEnv, useUserProfilePicture, useNumberFormat, useTranslations } from "@harea/component";
import { Outlet } from "react-router-dom";
import { useState } from "react";
import CreateDebauchageContainer from "./CreateDebauchageContainer";
import DebauchageDetailDialog from "../components/DebauchageDetailDialog";
import { DebauchagesQuery } from "../queries/DebauchagesQuery";
import { TDebauchage } from "@harea/component";
import moment from "moment";

const DebauchageContainer = () => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedDebauchage, setSelectedDebauchage] = useState<TDebauchage | undefined>();
  const profilePictureUrl = useUserProfilePicture();
  const { runtimeEnv } = useRuntimeEnv(true);
  const formatCurrency = useNumberFormat({
    style: "currency",
    currency: "MGA",
  });
  const [VIEW, DRAFT, PENDING, VALIDATED, COMPLETED, POACHING, CREATE, EMPLOYEE, DEPARTURE_TYPE, EFFECTIVE_DATE, TOTAL_INDEMNITIES, CREATION_DATE, ACTION, STATUS, RESIGNATION, MISCONDUCT, SERIOUS_MISCONDUCT, CONTRACT_END, NEGOTIATED_RESIGNATION] = useTranslations(i18n);

  const getDepartureTypeLabel = (type?: string) => {
    const labels: Record<string, string> = {
      RESIGNATION: RESIGNATION,
      MISCONDUCT: MISCONDUCT,
      SERIOUS_MISCONDUCT: SERIOUS_MISCONDUCT,
      CONTRACT_END: CONTRACT_END,
      NEGOTIATED_RESIGNATION: NEGOTIATED_RESIGNATION,
    };
    return labels[type || ""] || type || "-";
  };

  const getDepartureTypeColor = (type?: string) => {
    const colors: Record<string, string> = {
      RESIGNATION: "bg-warning",
      MISCONDUCT: "bg-danger",
      SERIOUS_MISCONDUCT: "bg-danger",
      CONTRACT_END: "bg-info",
      NEGOTIATED_RESIGNATION: "bg-success",
    };
    return colors[type || ""] || "bg-gray-400";
  };

  const getStatusLabel = (status?: string) => {
    const labels: Record<string, string> = {
      DRAFT: DRAFT,
      PENDING: PENDING,
      VALIDATED: VALIDATED,
      COMPLETED: COMPLETED,
      POACHING: POACHING,
    };
    return labels[status || ""] || status || "-";
  };

  const getStatusColor = (status?: string) => {
    const colors: Record<string, string> = {
      DRAFT: "bg-gray-400",
      PENDING: "bg-warning",
      VALIDATED: "bg-success",
      COMPLETED: "bg-info",
      POACHING: "bg-primary",
    };
    return colors[status || ""] || "bg-gray-400";
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl">{POACHING}</h2>
          <Button
            className="btn-primary"
            label={CREATE}
            onClick={() => setShowCreateDialog(true)}
          />
        </div>

        <Table
          id="App.Harea.Debauchage.List"
          BoxProps={{
            className: "box-transparent shadow-none overflow-x-auto",
          }}
          selection={false}
          search={true}
          pagination={true}
          GQLRequest={DebauchagesQuery}
          defaultFilter={{
            orgId: runtimeEnv?.organisation?.id,
            page: 0,
            pageSize: 10,
            sort: [{ sort: "DESC", value: "createdAt" }],
          }}
          onEnableRequest={(params) => params?.instance?.variables?.orgId}
          columns={[
            {
              key: "employee",
              label: EMPLOYEE,
              value: (row: TDebauchage) => (
                <div className="flex items-center gap-3">
                  <img
                    src={profilePictureUrl(row?.personnel?.id || 0)}
                    alt={row?.personnel?.fullName}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <div className="flex flex-col">
                    <span className="font-medium text-sm whitespace-nowrap">
                      {row?.personnel?.fullName || "-"}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {row?.personnel?.matricule || "-"}
                    </span>
                  </div>
                </div>
              ),
              className: "min-w-[200px]",
            },
            {
              key: "departureType",
              label: DEPARTURE_TYPE,
              value: (row: TDebauchage) => (
                <Chip
                  label={getDepartureTypeLabel(row?.departureType)}
                  className={getDepartureTypeColor(row?.departureType)}
                />
              ),
              className: "min-w-[180px]",
            },
            {
              key: "effectiveDate",
              label: EFFECTIVE_DATE,
              value: (row: TDebauchage) =>
                row?.effectiveDate
                  ? moment(row.effectiveDate).format("DD/MM/YYYY")
                  : "-",
              className: "min-w-[120px]",
            },
            {
              key: "status",
              label: STATUS,
              value: (row: TDebauchage) => (
                <Chip
                  label={getStatusLabel(row?.status)}
                  className={getStatusColor(row?.status)}
                />
              ),
              className: "min-w-[100px]",
            },
            {
              key: "indemnities",
              label: TOTAL_INDEMNITIES,
              value: (row: TDebauchage) =>
                row?.calculatedIndemnities?.total
                  ? formatCurrency(row.calculatedIndemnities.total)
                  : "-",
              className: "min-w-[120px]",
            },
            {
              key: "createdAt",
              label: CREATION_DATE,
              value: (row: TDebauchage) => {
                if (!row?.createdAt) return "-";
                const date = moment(typeof row.createdAt === 'number' ? row.createdAt : parseInt(row.createdAt));
                return date.isValid() ? date.format("DD/MM/YYYY") : "-";
              },
              className: "min-w-[120px]",
            },
            {
              key: "action",
              label: ACTION,
              className: "w-[80px]",
              value: (row: TDebauchage) => (
                <button
                  className="text-[rgb(112,28,16)] hover:text-[rgb(80,20,12)]"
                  title={VIEW}
                  onClick={() => {
                    setSelectedDebauchage(row);
                    setShowDetailDialog(true);
                  }}
                >
                  <i className="fas fa-eye" />
                </button>
              ),
            },
          ]}
        />
      </div>

      <GenericSuspense>
        <Outlet />
      </GenericSuspense>

      <CreateDebauchageContainer
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
      />

      <DebauchageDetailDialog
        open={showDetailDialog}
        onClose={() => {
          setShowDetailDialog(false);
          setSelectedDebauchage(undefined);
        }}
        debauchage={selectedDebauchage}
      />
    </>
  );
};

export default DebauchageContainer;

const i18n = ["std_view", "std_draft", "std_pending", "std_validated", "std_completed", "std_poaching", "std_create", "std_employee", "std_departure_type", "std_effective_date", "std_total_indemnities", "std_creation_date", "std_action", "std_status", "std_resignation", "std_misconduct", "std_serious_misconduct", "std_contract_end", "std_negotiated_resignation"];
